// src/pages/EditEvent.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { API_BASE } from "../services/api";

const MAX_IMG_MB = 20;
const bytesToKb = (n) => Math.round(n / 1024);
const pad2 = (n) => String(n).padStart(2, "0");

/* ---------------- Normalizers (tolerant to arrays/objects/strings) ---------------- */

function dateForInput(v) {
  // Accept "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss[Z]" OR [YYYY, M, D] OR {year,month*,day*}
  if (!v) return "";
  if (typeof v === "string") return v.slice(0, 10);
  if (Array.isArray(v) && v.length >= 3) {
    const [y, m, d] = v;
    if (y && m && d) return `${y}-${pad2(m)}-${pad2(d)}`;
  }
  if (typeof v === "object") {
    const y = v.year ?? v.Y ?? v.y;
    const m = v.monthValue ?? v.month ?? v.M ?? v.m;
    const d = v.dayOfMonth ?? v.day ?? v.D ?? v.d;
    if (y && m && d) return `${y}-${pad2(m)}-${pad2(d)}`;
  }
  return "";
}

function timeForInput(v) {
  // Accept "YYYY-MM-DDTHH:mm[:ss][Z]" OR "HH:mm[:ss]" OR [H, M, (S)]
  if (!v) return "";
  if (Array.isArray(v) && v.length >= 2) {
    const [H, M] = v;
    if (Number.isFinite(H) && Number.isFinite(M)) return `${pad2(H)}:${pad2(M)}`;
  }
  const s = String(v);
  const t = s.includes("T") ? s.split("T")[1] : s;
  const mm = t.slice(0, 5);
  return /^\d{2}:\d{2}$/.test(mm) ? mm : "";
}

function addDays(dateStr, days) {
  if (!dateStr) return "";
  const [y, m, d] = String(dateStr).split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, (d || 1) + days);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

function joinLocal(dateStr, timeStr) {
  if (!dateStr || !timeStr) return "";
  return `${dateStr}T${timeStr}:00`;
}

/* ---------------------------------------------------------------------------------- */

export default function EditEvent() {
  const { id } = useParams();
  const [qs] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const [form, setForm] = useState({
    title: "",
    description: "",
    location: "",
    date: "",
    startTime: "",
    endTime: "",
    totalStandardTickets: 0,
    standardTicketPrice: 0,
    totalVipTickets: 0,
    vipTicketPrice: 0,
    status: "PLANNED",
  });

  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [removeImage, setRemoveImage] = useState(false);

  const wantingPublish = useMemo(() => qs.get("publish") === "1", [qs]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const { data: ev } = await api.get(`/api/admin/events/${id}`);

      // Map backend → form with hardening for data shapes
      const mapped = {
        title: ev?.title ?? "",
        description: ev?.description ?? "",
        location: ev?.location ?? "",
        date: dateForInput(ev?.date),
        startTime: timeForInput(ev?.startTime),
        endTime: timeForInput(ev?.endTime),
        totalStandardTickets: Number(ev?.totalStandardTickets ?? 0),
        standardTicketPrice: Number(ev?.standardTicketPrice ?? ev?.standardPrice ?? 0),
        totalVipTickets: Number(ev?.totalVipTickets ?? 0),
        vipTicketPrice: Number(ev?.vipTicketPrice ?? ev?.vipPrice ?? 0),
        status: ev?.status ?? "PLANNED",
      };

      setForm(mapped);

      // Prefer explicit imageUrl; otherwise your media endpoint
      const url = ev?.imageUrl
        ? (String(ev.imageUrl).startsWith("http") ? ev.imageUrl : `${API_BASE}${ev.imageUrl}`)
        : `${API_BASE}/api/media/events/${id}`;

      setPreviewUrl(`${url}?v=${Date.now()}`);
    } catch (e) {
      console.error("Failed to load event:", e);
      setErr("Failed to load event.");
    } finally {
      setLoading(false);
    }
  }

  const onChange = (e) => {
    const { name, value, type } = e.target;
    const isNum = type === "number" || name.includes("Tickets") || name.includes("Price");
    setForm((f) => ({ ...f, [name]: isNum ? Number(value) : value }));
  };

  const onImage = (e) => {
    setErr("");
    const f = e.target.files?.[0];
    if (!f) {
      setImageFile(null);
      return;
    }
    const sizeMb = f.size / (1024 * 1024);
    if (sizeMb > MAX_IMG_MB) {
      setErr(`Image is too large (max ${MAX_IMG_MB}MB).`);
      return;
    }
    if (previewUrl && previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setRemoveImage(false);
    setImageFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const clearSelected = () => {
    if (previewUrl && previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setImageFile(null);
    setPreviewUrl(`/api/media/events/${id}`);
  };

  const validate = () => {
    const missing = [];
    if (!form.title.trim()) missing.push("title");
    if (!form.description.trim()) missing.push("description");
    if (!form.location.trim()) missing.push("location");
    if (!form.date) missing.push("date");
    if (!form.startTime) missing.push("start time");
    if (!form.endTime) missing.push("end time");
    if (!(Number(form.standardTicketPrice) > 0)) missing.push("standard ticket price");
    if (!(Number(form.vipTicketPrice) > 0)) missing.push("vip ticket price");
    const total = Number(form.totalStandardTickets || 0) + Number(form.totalVipTickets || 0);
    if (total <= 0) missing.push("ticket totals (std or VIP)");
    return missing;
  };

  const save = async (targetStatus) => {
    setErr("");
    setMsg("");

    const missing = targetStatus === "PUBLISHED" ? validate() : [];
    if (missing.length) {
      setErr(`Please complete: ${missing.join(", ")}`);
      return;
    }

    const endDateStr = form.endTime < form.startTime ? addDays(form.date, 1) : form.date;

    const data = {
      title: form.title.trim(),
      description: form.description,
      location: form.location.trim(),
      date: form.date,
      startTime: joinLocal(form.date, form.startTime),
      endTime: joinLocal(endDateStr, form.endTime),
      totalStandardTickets: Number(form.totalStandardTickets || 0),
      standardTicketPrice: Number(form.standardTicketPrice || 0),
      totalVipTickets: Number(form.totalVipTickets || 0),
      vipTicketPrice: Number(form.vipTicketPrice || 0),
      status: targetStatus,
      imageUrl: removeImage ? "REMOVE" : undefined,
    };

    try {
      const fd = new FormData();
      fd.append("data", new Blob([JSON.stringify(data)], { type: "application/json" }));
      if (!removeImage && imageFile) {
        fd.append("image", imageFile);
      }
      await api.put(`/api/admin/events/${id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMsg(targetStatus === "PUBLISHED" ? "✅ Published!" : "✅ Saved draft.");
      navigate("/admin/events");
    } catch (e) {
      const raw = e?.response?.data;
      setErr(typeof raw === "string" ? raw : raw?.message || "Failed to save changes.");
    }
  };

  if (loading) {
    return (
      <div className="page">
        <section className="card">Loading…</section>
      </div>
    );
  }
  if (err && !msg && !form.title) {
    return (
      <div className="page">
        <section className="card text-red-200">{err}</section>
      </div>
    );
  }

  return (
    <div className="page space-y-6">
      <section className="card">
        <div className="md:flex md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">Edit Event</h1>
            <p className="mt-1 text-slate-300">
              Update details. Publishing requires all fields to be complete.
            </p>
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={() => navigate(-1)}>
              Back
            </button>
            <button className="btn-secondary" onClick={() => save("PLANNED")}>
              Save as Draft
            </button>
            <button className="btn-primary" onClick={() => save("PUBLISHED")}>
              {wantingPublish ? "Publish now" : "Publish"}
            </button>
          </div>
        </div>

        {(msg || err) && (
          <div className="mt-3">
            {msg && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-emerald-200">
                {msg}
              </div>
            )}
            {err && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-200">
                {err}
              </div>
            )}
          </div>
        )}
      </section>

      <form className="grid gap-4 md:grid-cols-3" onSubmit={(e) => e.preventDefault()}>
        <section className="card md:col-span-2">
          <h2 className="text-xl font-semibold text-white">Event Details</h2>
          <div className="mt-4 grid gap-3">
            <Field label="Title *">
              <input name="title" className="input w-full" value={form.title} onChange={onChange} required />
            </Field>
            <Field label="Description *">
              <textarea name="description" className="input w-full min-h-[120px]" value={form.description} onChange={onChange} required />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Location *">
                <input name="location" className="input w-full" value={form.location} onChange={onChange} required />
              </Field>
              <Field label="Date *">
                <input type="date" name="date" className="input w-full" value={form.date} onChange={onChange} required />
              </Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Start Time *">
                <input type="time" name="startTime" className="input w-full" value={form.startTime} onChange={onChange} required />
              </Field>
              <Field label="End Time *">
                <input type="time" name="endTime" className="input w-full" value={form.endTime} onChange={onChange} required />
              </Field>
            </div>
            <p className="text-xs text-slate-400">
              If end time is earlier than start time, we’ll treat it as overnight.
            </p>
          </div>
        </section>

        <section className="card">
          <h2 className="text-xl font-semibold text-white">Artwork</h2>
          <div className="mt-4 grid gap-3">
            <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
              <div className="aspect-[4/3] w-full">
                {previewUrl ? (
                  <img src={previewUrl} alt="Event artwork" className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-full w-full place-items-center text-sm text-slate-400">No image</div>
                )}
              </div>
              <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-white/10" />
            </div>

            <label className="text-[11px] uppercase tracking-wider text-slate-400">
              Upload image (max {MAX_IMG_MB}MB)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={onImage}
              className="input file:mr-3 file:rounded-lg file:border-0 file:bg-white/10 file:px-3 file:py-1.5 file:text-slate-100 hover:file:bg-white/15"
            />

            {(imageFile || previewUrl) && (
              <div className="flex items-center justify-between text-xs text-slate-400">
                <div className="truncate">
                  {imageFile ? `${imageFile.name} • ${bytesToKb(imageFile.size)} KB` : "Existing image"}
                </div>
                <div className="flex items-center gap-3">
                  {imageFile && (
                    <button type="button" className="btn-secondary !px-2 !py-1" onClick={clearSelected}>
                      Clear
                    </button>
                  )}
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={removeImage}
                      onChange={(e) => {
                        setRemoveImage(e.target.checked);
                        if (e.target.checked) setImageFile(null);
                      }}
                    />
                    Remove image
                  </label>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="card md:col-span-3">
          <h2 className="text-xl font-semibold text-white">Tickets & Pricing</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <h3 className="font-medium text-white">Standard</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Field label="Total *">
                  <input
                    type="number"
                    min={0}
                    step={1}
                    name="totalStandardTickets"
                    className="input w-full"
                    value={form.totalStandardTickets}
                    onChange={onChange}
                    required
                  />
                </Field>
                <Field label="Price (£) *">
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    name="standardTicketPrice"
                    className="input w-full"
                    value={form.standardTicketPrice}
                    onChange={onChange}
                    required
                  />
                </Field>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <h3 className="font-medium text-white">VIP</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Field label="Total *">
                  <input
                    type="number"
                    min={0}
                    step={1}
                    name="totalVipTickets"
                    className="input w-full"
                    value={form.totalVipTickets}
                    onChange={onChange}
                    required
                  />
                </Field>
                <Field label="Price (£) *">
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    name="vipTicketPrice"
                    className="input w-full"
                    value={form.vipTicketPrice}
                    onChange={onChange}
                    required
                  />
                </Field>
              </div>
            </div>
          </div>
        </section>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-wider text-slate-400">{label}</span>
      {children}
    </label>
  );
}
